import React, { useState } from 'react';
import { Languages } from 'lucide-react';
import { SEO } from '../components/SEO';

function CirilicaLatinица() {
  const [text, setText] = useState<string>('');
  const [result, setResult] = useState<string>('');

  // Mapping of Cyrillic to Latin characters
  const cyrillicToLatin: { [key: string]: string } = {
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'ђ': 'đ', 'е': 'e',
    'ж': 'ž', 'з': 'z', 'и': 'i', 'ј': 'j', 'к': 'k', 'л': 'l', 'љ': 'lj',
    'м': 'm', 'н': 'n', 'њ': 'nj', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's',
    'т': 't', 'ћ': 'ć', 'у': 'u', 'ф': 'f', 'х': 'h', 'ц': 'c', 'ч': 'č',
    'џ': 'dž', 'ш': 'š',
    'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D', 'Ђ': 'Đ', 'Е': 'E',
    'Ж': 'Ž', 'З': 'Z', 'И': 'I', 'Ј': 'J', 'К': 'K', 'Л': 'L', 'Љ': 'Lj',
    'М': 'M', 'Н': 'N', 'Њ': 'Nj', 'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S',
    'Т': 'T', 'Ћ': 'Ć', 'У': 'U', 'Ф': 'F', 'Х': 'H', 'Ц': 'C', 'Ч': 'Č',
    'Џ': 'Dž', 'Ш': 'Š'
  };

  const convertToLatin = (text: string): string => {
    return text.split('').map(char => cyrillicToLatin[char] || char).join('');
  };

  const handleConvert = () => {
    const converted = convertToLatin(text);
    setResult(converted);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
  };

  const handleClear = () => {
    setText('');
    setResult('');
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Ćirilica u Latinicu",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni online prevoditelj ćirilice u latinicu. Jednostavan za korištenje, podržava srpsku i makedonsku ćirilicu.",
    "url": "https://online-kalkulator.com/cirilica-latinica",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Pretvorba ćirilice u latinicu",
      "Podrška za više jezika",
      "Trenutni prijevod",
      "Kopiranje rezultata",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Ćirilica u Latinicu | Online Prijevod Ćiriličnog Pisma"
        description="Besplatni online prevoditelj ćirilice u latinicu. Jednostavan za korištenje, podržava srpsku i makedonsku ćirilicu."
        canonicalUrl="/cirilica-latinica"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Languages className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Ćirilica u Latinicu</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Converter Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unesite ćirilični tekst
                </label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="w-full h-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Упишите или налепите ћирилични текст овде..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={handleConvert}
                  className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Prevedi
                </button>
                <button
                  onClick={handleClear}
                  className="py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                >
                  Očisti
                </button>
              </div>

              {/* Result */}
              {result && (
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Latinični tekst
                  </label>
                  <div className="relative">
                    <textarea
                      value={result}
                      readOnly
                      className="w-full h-32 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg"
                    />
                    <button
                      onClick={handleCopy}
                      className="absolute top-2 right-2 p-2 bg-white text-gray-600 rounded border border-gray-300 hover:bg-gray-100 transition"
                      title="Kopiraj tekst"
                    >
                      Kopiraj
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Prevoditelju</h2>
                <p className="text-gray-600 leading-relaxed">
                  Ovaj online prevoditelj omogućuje vam jednostavno pretvaranje ćiriličnog teksta u
                  latinicu. Podržava srpsku i makedonsku ćirilicu, uključujući sva posebna slova i
                  znakove. Prevoditelj zadržava originalno formatiranje teksta i interpunkciju.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite ili zalijepite ćirilični tekst u gornje polje</li>
                  <li>Kliknite na gumb "Prevedi"</li>
                  <li>Prevedeni tekst će se pojaviti u donjem polju</li>
                  <li>Koristite gumb "Kopiraj" za kopiranje rezultata</li>
                  <li>Gumb "Očisti" briše oba polja za novi prijevod</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Podržana Slova</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Ćirilična Slova</h3>
                    <p className="text-gray-600 font-mono">
                      А Б В Г Д Ђ Е Ж З И Ј К Л Љ М Н Њ О П Р С Т Ћ У Ф Х Ц Ч Џ Ш
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Latinična Slova</h3>
                    <p className="text-gray-600 font-mono">
                      A B V G D Đ E Ž Z I J K L Lj M N Nj O P R S T Ć U F H C Č Dž Š
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Značajke</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Trenutni prijevod bez čekanja</li>
                  <li>Podržava velika i mala slova</li>
                  <li>Zadržava originalno formatiranje</li>
                  <li>Radi i sa srpskom i makedonskom ćirilicom</li>
                  <li>Jednostavno kopiranje prevedenog teksta</li>
                  <li>Besplatno korištenje bez ograničenja</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Prevoditelj zadržava interpunkciju i posebne znakove</li>
                  <li>Brojevi i simboli ostaju nepromijenjeni</li>
                  <li>Podržava kopiranje i lijepljenje teksta</li>
                  <li>Nema ograničenja duljine teksta</li>
                  <li>Radi i bez internetske veze nakon učitavanja stranice</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CirilicaLatinица;